import type { Article } from "./types";

export const article: Article = {
  slug: "dopamine-detox-christian",
  title: "A dopamine detox, for Christians: what it is and is not",
  h1: "A dopamine detox, for Christians",
  description:
    "The neuroscience behind the trend is mostly wrong, and the practice underneath it is ancient. What a week of it actually involves, and what fasting adds that a detox does not.",
  targets: [
    "dopamine detox christian",
    "christian dopamine fast",
    "does a dopamine detox work",
  ],
  published: "2026-08-26",
  standfirst:
    "You cannot detox from a neurotransmitter your body makes on purpose. What you can do is stop feeding one particular loop, and that turns out to be an old idea with a new name.",
  takeaways: [
    "There is no such thing as detoxing from dopamine — it is a signalling chemical your brain requires for movement, motivation and learning, and lowering it is not a goal any doctor would set.",
    "What the practice actually reduces is the frequency of unpredictable rewards, which is the specific pattern that keeps a feed compelling, and that part is real.",
    "The Christian version of this already exists and is older by two millennia: fasting removes something in order to make room for something, and the second half is what distinguishes it from a productivity hack.",
    "A week is long enough to notice withdrawal-shaped restlessness on day two and a genuinely different relationship with boredom by day five.",
  ],
  body: [
    {
      type: "p",
      text: "The phrase is wrong and the practice is not. Dopamine is not a pleasure chemical you accumulate and flush; it is a signalling molecule involved in movement, learning and — most relevantly here — the anticipation of a reward rather than the reward itself. Someone with genuinely low dopamine is not serene and focused. They are, in the clinical extreme, unable to initiate movement. Nobody is detoxing from that.",
    },
    {
      type: "p",
      text: "What the popular version gets right, underneath the vocabulary, is the mechanism it is aiming at. A feed is compelling not because it is enjoyable but because it is unpredictable: most pulls give you nothing much, and occasionally one gives you something. That schedule — intermittent, variable, never quite exhausted — is the most persistent reinforcement pattern known, and it is the reason you can close an app, feel nothing in particular, and reopen it within the minute.",
    },
    {
      type: "h2",
      id: "what-it-actually-does",
      text: "What a week without it actually does",
    },
    {
      type: "p",
      text: "Not a reset. There is no counter that goes back to zero, and anyone promising a rewired brain in seven days is selling something. What a week does is quieter and more useful: it removes the easiest possible answer to a small uncomfortable feeling, often enough and for long enough that you start noticing the feeling itself.",
    },
    {
      type: "ol",
      items: [
        "Days one and two are restlessness. Not craving in any dramatic sense — a persistent sense that you are forgetting something, and a hand that arrives at a pocket without being sent.",
        "Day three is usually when the day gets longer. The hours that were being spent in fragments reappear, and the first honest reaction to them is boredom rather than gratitude.",
        "Days four and five are where anything interesting happens. Boredom that is not immediately answered turns into thinking, and for most people it also turns, unprompted, into prayer.",
        "Day six or seven is the decision. Almost nobody goes back to nothing; the useful question is which two or three things come back and under what conditions.",
      ],
    },
    {
      type: "note",
      text: "If day two feels like genuine distress rather than restlessness — panic, dread, a compulsion you cannot sit through — that is worth talking to somebody about. It is information, not a failure of the week.",
    },
    {
      type: "h2",
      id: "fasting-is-the-older-word",
      text: "Fasting is the older word for this",
    },
    {
      type: "p",
      text: "Every element of the modern practice is present in Christian fasting, and one element is present there that the modern version drops. Fasting removes something — food, usually, but the tradition has never been narrow about it — for a defined period, in order to make room. The room is the point. A fast with nothing in the space it clears is a diet with religious language attached.",
    },
    {
      type: "verse",
      verse: {
        text: "But you, when you fast, anoint your head and wash your face, so that you are not seen by men to be fasting.",
        ref: "Matthew 6:17–18",
      },
    },
    {
      type: "p",
      text: "The instruction is about concealment, which is worth sitting with in a category that now runs almost entirely on announcement. A dopamine detox posted about is a different act from one nobody knows about, and the passage is unusually direct about which of the two it has in mind.",
    },
    {
      type: "p",
      text: "The practical difference this makes is in what you plan. A secular version plans the removal: delete the apps, grayscale the screen, put the phone in a drawer. A fast plans the replacement first — what the hours are for — and the removal follows from it. Anyone who has abandoned one of these on day three can usually trace it to that order being reversed, and {the-social-media-fast-guide} sets out the version that survives contact with an ordinary week.",
      links: { "the-social-media-fast-guide": "/blog/social-media-fast-guide" },
    },
    {
      type: "h2",
      id: "what-to-actually-remove",
      text: "What to remove, and what not to",
    },
    {
      type: "p",
      text: "The maximalist versions — no music, no conversation, no reading, staring at a wall — are a misunderstanding dressed as rigour. The variable-reward loop is not in a book or in a friend; it is in a small number of specific products, and removing everything else mostly guarantees the week ends early.",
    },
    {
      type: "ul",
      items: [
        "Remove: the two or three apps you open without deciding to. You know which they are, and the list is shorter than you would guess.",
        "Remove: the phone from the bedroom, which is where the loop is least resistible and most expensive.",
        "Keep: messages with actual people, music, books, maps, anything with an end.",
        "Keep: your normal week. A fast that requires a free diary is a fast you will schedule and never take.",
      ],
    },
    {
      type: "p",
      text: "The distinction that does the work is whether a thing terminates. A chapter ends. An album ends. A phone call ends. A feed is built specifically not to, and that is the whole of the difference between something you use and something that uses the time you did not decide to give it.",
    },
    {
      type: "h2",
      id: "after-the-week",
      text: "The part after the week, which is the actual point",
    },
    {
      type: "p",
      text: "A week off changes almost nothing on its own. Every study of abstinence in every category says the same unglamorous thing: the interval matters far less than what is in place when it ends. People return to the same phone, in the same evenings, with the same two apps one tap from the home screen, and the week becomes a pleasant memory by the following Thursday.",
    },
    {
      type: "p",
      text: "So decide the terms before you finish. Which apps come back, at what times of day, and what stands between you and them the rest of the time. That last one has to be automatic, because the decision is otherwise being made at eleven at night by the most tired version of you — which is exactly the argument in {why-screen-time-limits-stop-working}, and it is the reason Bible Shield asks for a short prayer at the moment of reaching rather than showing a limit you can dismiss.",
      links: {
        "why-screen-time-limits-stop-working":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "p",
      text: "And if you want the honest version of where your own week goes before you fast from anything, {the-phone-and-faith-test} is twelve questions and about four minutes. Fasting from the wrong thing is the most common way this ends in nothing.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
  ],
  faq: [
    {
      q: "Is a dopamine detox scientifically real?",
      a: "The name is not. You cannot reset dopamine by avoiding your phone, and it is not the pleasure chemical it is popularly described as — it is tied more closely to anticipation than to reward. What is real is the effect of removing intermittent variable rewards for a period: a behavioural change, not a chemical one.",
    },
    {
      q: "How long should a Christian dopamine fast be?",
      a: "A week is the shortest period long enough to get past the restlessness of the first two days and reach the part where the extra hours turn into something. Longer is not obviously better; what matters more is deciding in advance what the space is for and what happens on the day it ends.",
    },
    {
      q: "Is fasting from a phone the same as fasting from food?",
      a: "Traditionally food is the primary form, and most Christian traditions treat it that way. The structure transfers, though: something is removed for a set period so that something else has room. If you are fasting from a screen, the tradition would say the second half is not optional.",
    },
    {
      q: "What should I do with the time?",
      a: "Decide before you start, and make it specific. Vague intentions to pray more collapse into whatever is nearest, which is the phone you just put down. A book, a fixed twenty minutes of scripture, a walk with somebody — anything with a beginning and an end.",
    },
  ],
  related: [
    "social-media-fast-guide",
    "screen-time-limits-dont-work",
    "digital-sabbath",
  ],
};
